export type BrandColors = {
  primary: string;
  positive: string;
  negative: string;
  foreground: string;
  stroke: string;
};

export type ChartTheme = {
  dark: boolean;
  colors: BrandColors;
  textColor: string;
  axisLineColor: string;
};

export function isDark(): boolean {
  const gt = globalThis as { getTheme?: () => string };
  return typeof gt.getTheme === 'function'
    ? gt.getTheme() === 'dark'
    : document.documentElement.classList.contains('dark');
}

/**
 * Resolve a CSS custom property to an rgb() string that ECharts can parse.
 * ECharts' internal colour parser doesn't support oklch(), so we let the browser
 * resolve the variable by setting it on a hidden element and reading the computed value.
 */
export function resolveCssColor(cssVar: string, fallback: string): string {
  try {
    const tmp = document.createElement('div');
    tmp.style.cssText = `color: var(${cssVar}); position: absolute; visibility: hidden;`;
    document.body.appendChild(tmp);
    const color = getComputedStyle(tmp).color; // always rgb(...) in modern browsers
    document.body.removeChild(tmp);
    return color || fallback;
  } catch {
    return fallback;
  }
}

// Resolve brand tokens once, then refresh on theme change via resetBrandColorsCache.
let _colors: BrandColors | null = null;

export function brandColors(): BrandColors {
  if (!_colors) {
    _colors = {
      primary: resolveCssColor('--color-primary', '#e8722a'),
      positive: resolveCssColor('--color-positive', '#2ca85d'),
      negative: resolveCssColor('--color-negative', '#e04820'),
      foreground: resolveCssColor('--color-foreground', '#1a1a1a'),
      stroke: resolveCssColor('--color-stroke-subtle', 'rgba(26,26,26,0.15)'),
    };
  }
  return _colors;
}

/** Reset cached brand colors (for tests). */
export function resetBrandColorsCache(): void {
  _colors = null;
}

export function chartTheme(): ChartTheme {
  const dark = isDark();
  const colors = brandColors();
  const textColor = dark ? 'rgba(255,255,255,0.45)' : 'rgba(26,26,26,0.45)';
  const axisLineColor = dark ? 'rgba(255,255,255,0.10)' : colors.stroke;
  return { dark, colors, textColor, axisLineColor };
}

export const seriesPalette = (colors: BrandColors): string[] => [
  colors.primary,
  colors.positive,
  colors.negative,
  '#6366f1',
  '#f59e0b',
  '#06b6d4',
];
